/**
 * Analytics for the app: Google Analytics 4 AND our own first-party tracker.
 *
 * The `track()` taxonomy below is unchanged, so the ~60 call sites across the
 * app keep working exactly as they did. Every one of them also reaches our own
 * store, where it can be joined to the same person's page views, scrolling,
 * clicks, outcomes, errors and — with consent — a recording of the session.
 * GA4 can tell you 900 people ran a search; it cannot tell you whether any of
 * them got something useful.
 *
 * Two rules hold everywhere in this file:
 *   - it never throws, and it is never awaited on a path the user is waiting on;
 *   - a feature run is recorded BEFORE the work and resolved after, so a run
 *     that never finished shows up as abandoned rather than as missing.
 *
 * Event taxonomy (all custom events, so they can be built into GA4 reports):
 *   screen_view        screen_name
 *   first_visit_custom / second_visit / return_visit   visit_count
 *   select_mode        mode = bot | online | friends | lessons
 *   game_start         mode, level?, side?
 *   game_end           mode, result = win|lose|draw, reason, moves, level?
 *   first_move         mode
 *   hint_used          level, ply
 *   undo_used          ply
 *   history_review     mode
 *   resign_click / rematch_click            mode
 *   matchmaking_start / matchmaking_matched
 *   room_created / room_joined
 *   lesson_start / lesson_complete / lesson_wrong    lesson_id, group
 *   language_change    lang
 *   piece_style_change style
 *   sound_toggle       enabled
 */

import { initTracker, type Run, type Tracker } from "@xq/analytics-browser";
import { getToken } from "./account.js";

type Params = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

let tracker: Tracker | null = null;

// ---------------------------------------------------------------- GA4

export function track(event: string, params: Params = {}): void {
  try {
    window.gtag?.("event", event, params);
  } catch {
    /* analytics must never break the app */
  }
  try {
    mirror(event, params);
  } catch {
    /* ignore */
  }
}

export function setUserProps(props: Params): void {
  try {
    window.gtag?.("set", "user_properties", props);
  } catch {
    /* ignore */
  }
}

export function screen(name: string): void {
  track("screen_view", { screen_name: name });
}

/**
 * Each page of the app is a real URL, so each one reports a GA4 `page_view` the
 * way a multi-page site would — gtag only sends the first one by itself. The
 * same call starts a first-party page view, which ends the previous one with
 * its final scroll depth and active time.
 */
export function pageView(path: string, title: string): void {
  track("page_view", { page_path: path, page_location: location.origin + path, page_title: title });
  tracker?.pageView(path);
}

// ---------------------------------------------------------------- first-party

/** Feature keys the collector accepts. Anything else is dropped server-side. */
export type FeatureKey =
  | "bot_game" | "online_game" | "friend_game" | "lesson" | "puzzle" | "analysis"
  | "engine_hint" | "matchmaking" | "room" | "signup" | "login" | "password_reset"
  | "search" | "board_theme" | "watch_game";

/** Browser-only signals. `copy` is the important one: it is the completion. */
export type EventName = "copy" | "cta" | "outbound" | "share" | "install" | "dismiss" | "pref" | "complete" | "download";

export type { Run };

const NOOP_RUN: Run = { ok() {}, empty() {}, failed() {} };

/**
 * Start a feature run. Call the returned handle when you know the outcome; a run
 * that is never resolved stays visible in the console as abandoned, which is
 * how abandonment is measured without a second table.
 */
export function action(key: FeatureKey, input?: string): Run {
  return tracker?.action(key, input) ?? NOOP_RUN;
}

/** A one-shot action whose outcome is already known. */
export function actionDone(key: FeatureKey, input: string | undefined, result: string): void {
  action(key, input).ok(result);
}

/** A browser-only signal: a copy, a call-to-action click, a preference change. */
export function event(name: EventName | string, target: string): void {
  tracker?.event(name, target);
}

/** A form step finished without a real <form> submit (the auth cards, the reset flow). */
export function formSubmitted(form: string, step?: string): void {
  tracker?.formSubmit(form, step);
}

/**
 * Report an error we caught ourselves, with its real stack.
 *
 * The window-level listener cannot see inside a web worker: what it recorded for
 * an engine crash was a filename and a column, which is not enough to find
 * anything. Anything caught in a try/catch is reported through here.
 */
export function reportError(message: string, stack?: string): void {
  tracker?.error(message, stack);
}

// ---------------------------------------------------------------- mirroring

/**
 * Map the existing GA4 event taxonomy onto the first-party model, so the app's
 * own call sites did not have to change.
 *
 * Runs that the server already records from an authenticated request — sign-up,
 * log-in, online and friend games — are deliberately NOT mirrored here: the
 * server writes those rows itself, and a second client-side copy would double
 * every count.
 */
const openRuns = new Map<string, Run>();

function mirror(name: string, p: Params): void {
  const s = (k: string): string | undefined => (p[k] == null ? undefined : String(p[k]));

  switch (name) {
    case "game_start": {
      // Only the bot game is client-side; online and friend games are opened by
      // the server, which knows their real outcome.
      if (s("mode") !== "bot") return;
      const run = action("bot_game", `level ${s("level") ?? "?"} as ${s("side") ?? "?"}`);
      openRuns.set("bot_game", run);
      return;
    }
    case "game_end": {
      if (s("mode") !== "bot") return;
      const run = openRuns.get("bot_game");
      openRuns.delete("bot_game");
      run?.ok(`${s("result") ?? "?"} by ${s("reason") ?? "?"}, ${s("moves") ?? "?"} moves`);
      return;
    }

    case "lesson_start": {
      const id = s("lesson_id") ?? "lesson";
      openRuns.set(`lesson:${id}`, action("lesson", id));
      return;
    }
    case "lesson_complete": {
      const id = s("lesson_id") ?? "lesson";
      const run = openRuns.get(`lesson:${id}`);
      openRuns.delete(`lesson:${id}`);
      run?.ok("completed");
      return;
    }
    case "lesson_wrong": {
      // Not a failure of the lesson — but a lesson nobody ever completes after
      // getting it wrong is the thing this is here to make visible.
      event("dismiss", `lesson-wrong-${s("lesson_id") ?? "x"}`);
      return;
    }

    case "hint_used":
      actionDone("engine_hint", `level ${s("level") ?? "?"} ply ${s("ply") ?? "?"}`, "hint shown");
      return;

    case "matchmaking_start":
      openRuns.set("matchmaking", action("matchmaking", s("tc")));
      return;
    case "matchmaking_matched": {
      const run = openRuns.get("matchmaking");
      openRuns.delete("matchmaking");
      run?.ok("matched");
      return;
    }

    case "room_created":
    case "room_joined":
      actionDone("room", s("tc"), name === "room_created" ? "room created" : "room joined");
      return;

    case "board_theme_change":
      actionDone("board_theme", s("theme"), "theme applied");
      return;

    // The auth cards are not <form>s; their submit is reported here so the form
    // drop-off report sees the step complete.
    case "sign_up":
      formSubmitted("signup");
      return;
    case "login":
      formSubmitted("login");
      return;
    case "password_reset_request":
      formSubmitted("forgot", "email");
      return;
    case "password_reset_done":
      formSubmitted("forgot", "password");
      return;

    // Preferences and navigation choices are signals, not runs with outcomes.
    case "select_mode":
      event("cta", `mode-${s("mode") ?? "x"}`);
      return;
    case "theme_toggle":
    case "language_change":
    case "piece_style_change":
    case "sound_toggle":
    case "voice_toggle":
    case "hints_toggle":
    case "focus_toggle":
      event("pref", name.replace(/_/g, "-"));
      return;
    case "push_enabled":
      event("complete", "push-enabled");
      return;

    default:
      return;
  }
}

/**
 * Resolve anything still open when the page goes away. A tab closed mid-game is
 * genuinely an abandoned run, so it is left unresolved on purpose.
 */
function wireUnload(): void {
  addEventListener("pagehide", () => {
    openRuns.clear();
  });
}

const VISITS_KEY = "xq-visits";

/**
 * Counts visits locally so GA can segment first-time / second-time / returning
 * players, stamps the user's current settings as user properties, and starts the
 * first-party tracker.
 */
export function initAnalytics(extra: Params = {}): void {
  let n = 1;
  try {
    n = Number(localStorage.getItem(VISITS_KEY) || "0") + 1;
    localStorage.setItem(VISITS_KEY, String(n));
  } catch {
    /* storage unavailable */
  }
  const visitorType = n === 1 ? "first_time" : n === 2 ? "second_time" : "returning";
  setUserProps({ visit_count: n, visitor_type: visitorType, ...extra });
  track(n === 1 ? "first_visit_custom" : n === 2 ? "second_visit" : "return_visit", { visit_count: n });

  try {
    const host = location.hostname;
    const onSite = host === "play.xiangqimaster.com" || host.endsWith(".xiangqimaster.com");
    tracker = initTracker({
      site: "app",
      ...(onSite ? { cookieDomain: ".xiangqimaster.com" } : {}),
      token: getToken,
      // Player names, message threads and the account chip are personal; the
      // board and everything else is not.
      maskSelectors: [".acct-chip", ".acct-name", ".dm-body", ".dm-input", ".chat-line", ".profile-hero .handle", ".opp-row .name"],
      // The board is an SVG with listeners, and every menu row is a div with an
      // onclick: none of them may be counted as a dead click.
      interactiveSelectors: [".board", ".mode-card", ".tile", ".path-item", ".lb-row", ".gh-row", ".menu-row", ".seg-btn", ".dock-btn", ".mv"],
      privacyUrl: "https://xiangqimaster.com/privacy",
    });
    wireUnload();
  } catch {
    /* a browser that cannot do this still gets a working app */
  }
}
